import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Divider,
  MenuItem,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import {
  useCreateTaskMutation,
  useUpdateTaskMutation,
} from "../../features/tasks/tasksApiSlice";
import { useGetUsersCleanQuery } from "../../features/users/usersApiSlice";

const STATUS_OPTIONS = [
  { value: "NEU", label: "Neu" },
  { value: "IN_BEARBEITUNG", label: "In Bearbeitung" },
  { value: "IN_PRUEFUNG", label: "In Prüfung" },
  { value: "ERLEDIGT", label: "Erledigt" },
];

const PRIORITY_OPTIONS = [
  { value: "dringend", label: "Dringend" },
  { value: "hoch", label: "Hoch" },
  { value: "normal", label: "Normal" },
  { value: "niedrig", label: "Niedrig" },
];

const defaults = {
  title: "",
  description: "",
  status: "NEU",
  priority: "normal",
  dueDate: "",
  category: "",
  assignedTo: [],
};

export default function TaskFormDialog({ initialTask, onSaved, onClose }) {
  const dispatch = useDispatch();
  const isEdit = Boolean(initialTask);
  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const { data: users = [] } = useGetUsersCleanQuery();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: defaults });

  useEffect(() => {
    if (initialTask) {
      reset({
        title: initialTask.title || "",
        description: initialTask.description || "",
        status: initialTask.status || "NEU",
        priority: initialTask.priority || "normal",
        dueDate: initialTask.dueDate
          ? dayjs(initialTask.dueDate).format("YYYY-MM-DD")
          : "",
        category: initialTask.category || "",
        assignedTo: Array.isArray(initialTask.assignedTo)
          ? initialTask.assignedTo.map((u) => u._id || u)
          : [],
      });
    } else {
      reset(defaults);
    }
  }, [initialTask, reset]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        title: data.title?.trim(),
        description: data.description || "",
        status: data.status,
        priority: data.priority,
        dueDate: data.dueDate ? dayjs(data.dueDate).toDate() : null,
        category: data.category?.trim() || "",
        assignedTo: data.assignedTo || [],
      };

      if (isEdit) {
        await updateTask({ id: initialTask._id || initialTask.id, ...payload }).unwrap();
        dispatch(
          setDialog({
            type: "SUCCESS",
            title: "Aufgabe gespeichert",
            data: { successText: "Änderungen wurden gespeichert." },
          })
        );
      } else {
        await createTask(payload).unwrap();
        dispatch(
          setDialog({
            type: "SUCCESS",
            title: "Aufgabe erstellt",
            data: { successText: "Die Aufgabe wurde angelegt." },
          })
        );
      }
      onSaved?.();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Speichern fehlgeschlagen." },
        })
      );
    }
  };

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">
            {isEdit ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEdit
              ? "Aktualisieren Sie Details, Priorität und Zuweisung."
              : "Erstellen Sie eine Aufgabe und weisen Sie sie dem richtigen Team zu."}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)} className="form">
          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.light, 0.08),
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Stack spacing={1.5}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: "Bitte einen Titel angeben." }}
                  render={({ field }) => (
                    <TextField
                      label="Titel"
                      {...field}
                      error={!!errors.title}
                      helperText={errors.title?.message}
                      required
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Beschreibung"
                      {...field}
                      multiline
                      minRows={3}
                      fullWidth
                    />
                  )}
                />
              </Stack>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField select label="Status" {...field} sx={{ flex: 1 }} fullWidth>
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <TextField select label="Priorität" {...field} sx={{ flex: 1 }} fullWidth>
                    {PRIORITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Fällig am"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...field}
                    sx={{ flex: 1 }}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <TextField label="Kategorie" {...field} sx={{ flex: 1 }} fullWidth />
                )}
              />
            </Stack>

            <Divider />

            <Controller
              name="assignedTo"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label="Zuweisung"
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const user = users.find((u) => u._id === value);
                          const label = user
                            ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                            : value;
                          return <Chip key={value} label={label} size="small" />;
                        })}
                      </Box>
                    ),
                  }}
                  {...field}
                  fullWidth
                >
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                      {user.mail ? ` (${user.mail})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {users.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Keine Benutzer gefunden.
              </Typography>
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isCreating || isUpdating}
        >
          {isEdit ? "Speichern" : "Erstellen"}
        </Button>
      </DialogActions>
    </>
  );
}

TaskFormDialog.propTypes = {
  initialTask: PropTypes.object,
  onSaved: PropTypes.func,
  onClose: PropTypes.func,
};
